import { memo } from 'react';
import { AlertBanner, Button } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

import { PERMISSIONS } from '../../constants/permissions';
import Can from '../guards/Can';

/**
 * "<name> hasn't been invited yet" banner above the Members filters, with Send invitation and a
 * dismiss control. With several people waiting it says "N members haven't been invited yet" and
 * offers Send all invitations.
 *
 * @param {object} props
 * @param {{ displayName: string }} props.member
 * @param {number} [props.count] How many members are not invited (1: the single-person banner).
 * @param {boolean} [props.busy] An invitation is being sent: the action is disabled.
 * @param {'hide'|'lock'} [props.permissionMode] Overrides the default way the send action reads without the invite permission.
 * @param {() => void} props.onSend
 * @param {() => void} props.onDismiss
 */
const NotInvitedBanner = ({ member, count = 1, busy = false, permissionMode, onSend, onDismiss }) => {
  const { t } = useTranslation('people-access');
  const many = count > 1;

  return (
    <AlertBanner
      tone="warning"
      title={many ? t('members.notInvited.titleMany', { count }) : t('members.notInvited.title', { name: member.displayName })}
      action={
        // Only the action is gated: the banner keeps its width and Dismiss keeps working.
        <Can permission={PERMISSIONS.MEMBERS_INVITE} mode={permissionMode}>
          <Button variant="secondary" size="sm" onClick={onSend} disabled={busy} loading={busy}>
            {many ? t('members.notInvited.sendAll') : t('members.notInvited.send')}
          </Button>
        </Can>
      }
      onDismiss={onDismiss}
      dismissLabel={t('members.notInvited.dismiss')}
    >
      {t('members.notInvited.body')}
    </AlertBanner>
  );
};

export default memo(NotInvitedBanner);
